import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { cn } from '@/lib/utils';
import { TrendingUp, TrendingDown, Target, ShieldAlert, AlertTriangle, DollarSign } from 'lucide-react';
import { PriceHistoryChart } from './PriceHistoryChart';

interface Position {
  ticker: string;
  description: string;
  sector: string;
  quantity: number;
  avgCost: number;
  currentPrice: number;
  marketValue: number;
  costBasis: number;
  unrealizedPL: number;
  unrealizedPLPercent: number;
  currency: string;
}

interface StockScorecardDialogProps {
  position: Position | null;
  open: boolean;
  onClose: () => void;
}

const getCurrencySymbol = (currency: string): string => {
  const symbols: Record<string, string> = {
    'USD': '$',
    'EUR': '€',
    'CHF': 'CHF ',
    'GBP': '£',
    'JPY': '¥',
    'CAD': 'C$',
    'AUD': 'A$',
    'NZD': 'NZ$',
    'HKD': 'HK$',
    'SGD': 'S$',
    'SEK': 'kr',
    'NOK': 'kr',
    'DKK': 'kr',
  };
  return symbols[currency] || currency + ' ';
};

const formatCurrency = (value: number, currency: string = 'USD') => {
  const symbol = getCurrencySymbol(currency);
  return `${symbol}${value.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
};

export function IndividualStockScorecard({ position, open, onClose }: StockScorecardDialogProps) {
  if (!position) return null;

  const gainPct = position.unrealizedPLPercent;
  
  // Determine alert level
  const getAlertLevel = () => {
    if (gainPct >= 45) return {
      type: 'danger',
      color: 'bg-red-500',
      textColor: 'text-red-600',
      label: '🎯 TAKE PROFIT NOW',
      action: 'Sell 50% immediately to lock in 100% capital recovery',
    };
    if (gainPct >= 30) return {
      type: 'warning',
      color: 'bg-orange-500',
      textColor: 'text-orange-600',
      label: '⚡ PROFIT ZONE',
      action: 'Consider partial profit-taking (40% recovery target)',
    };
    if (gainPct >= 15) return {
      type: 'profit',
      color: 'bg-yellow-500',
      textColor: 'text-yellow-600',
      label: '💰 PROFIT ALERT',
      action: 'Monitor for profit-taking opportunity (20% recovery)',
    };
    if (gainPct < -5) return {
      type: 'danger',
      color: 'bg-red-600',
      textColor: 'text-red-700',
      label: '🛑 STOP LOSS ALERT',
      action: 'Consider cutting losses - below 5% threshold',
    };
    return {
      type: 'hold',
      color: 'bg-gray-400',
      textColor: 'text-gray-600',
      label: 'HOLD',
      action: 'Monitor position and wait for profit targets',
    };
  };

  const alertLevel = getAlertLevel();

  // Calculate profit targets (2:1, 3:1, 4:1, 6:1 reward:risk)
  const profitTargets = [
    { ratio: '2:1', price: position.avgCost * 1.10, gain: 10 },
    { ratio: '3:1', price: position.avgCost * 1.15, gain: 15 },
    { ratio: '4:1', price: position.avgCost * 1.20, gain: 20 },
    { ratio: '6:1', price: position.avgCost * 1.30, gain: 30 },
  ];

  const nextTarget = profitTargets.find(t => position.currentPrice < t.price);

  // Calculate stop losses
  const stopLosses = [
    { type: '5% Stop', price: position.avgCost * 0.95, description: 'Tight stop-loss' },
    { type: '10% Stop', price: position.avgCost * 0.90, description: 'Standard stop-loss' },
    { type: 'Technical', price: position.avgCost * 0.92, description: 'Below swing low' },
  ];

  // Calculate recommended sell quantity
  const recommendedSellQty = gainPct >= 45 ? Math.floor(position.quantity * 0.5) : 
                            gainPct >= 30 ? Math.floor(position.quantity * 0.3) :
                            gainPct >= 15 ? Math.floor(position.quantity * 0.2) : 0;

  const sellValue = recommendedSellQty * position.currentPrice;

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-[95vw] max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="text-2xl flex items-center gap-3">
            <Target className="h-6 w-6 text-primary" />
            {position.ticker} - Trading Scorecard
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-6">
          {/* Alert Banner */}
          <Card className={cn("border-2", alertLevel.color.replace('bg-', 'border-'))}>
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <Badge className={cn(alertLevel.color, "text-white text-lg px-4 py-2")}>
                    {alertLevel.label}
                  </Badge>
                  <p className={cn("mt-2 text-sm font-medium", alertLevel.textColor)}>
                    {alertLevel.action}
                  </p>
                </div>
                <div className="text-right">
                  <div className="text-sm text-muted-foreground">Current Gain/Loss</div>
                  <div className={cn("text-3xl font-bold", gainPct >= 0 ? 'text-green-600' : 'text-red-600')}>
                    {gainPct >= 0 ? '+' : ''}{gainPct.toFixed(2)}%
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Price History Chart */}
          <PriceHistoryChart ticker={position.ticker} currency={position.currency} />

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* Position Details */}
            <Card>
              <CardHeader>
                <CardTitle className="text-lg">Position Details</CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Description:</span>
                  <span className="font-medium text-right">{position.description}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Sector:</span>
                  <span className="font-medium">{position.sector || 'N/A'}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Quantity:</span>
                  <span className="font-medium">{position.quantity.toFixed(4)}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Avg Cost:</span>
                  <span className="font-medium">{formatCurrency(position.avgCost, position.currency)}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Current Price:</span>
                  <span className="font-bold">{formatCurrency(position.currentPrice, position.currency)}</span>
                </div>
                <div className="flex justify-between pt-3 border-t">
                  <span className="text-muted-foreground">Market Value:</span>
                  <span className="font-bold">{formatCurrency(position.marketValue, position.currency)}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Cost Basis:</span>
                  <span className="font-medium">{formatCurrency(position.costBasis, position.currency)}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Unrealized P/L:</span>
                  <span className={cn("font-bold", position.unrealizedPL >= 0 ? 'text-green-600' : 'text-red-600')}>
                    {formatCurrency(Math.abs(position.unrealizedPL), position.currency)}
                  </span>
                </div>
              </CardContent>
            </Card>

            {/* Recommended Action */}
            {recommendedSellQty > 0 && (
              <Card className="border-2 border-green-500 bg-green-500/5">
                <CardHeader>
                  <CardTitle className="text-lg flex items-center gap-2">
                    <DollarSign className="h-5 w-5 text-green-600" />
                    Recommended Action
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="p-4 bg-background rounded-lg border border-green-200">
                    <div className="text-sm text-muted-foreground mb-1">Shares to Sell</div>
                    <div className="text-3xl font-bold text-green-600">{recommendedSellQty}</div>
                    <div className="text-sm text-muted-foreground">
                      ({((recommendedSellQty / position.quantity) * 100).toFixed(0)}% of position)
                    </div>
                  </div>
                  <div className="p-4 bg-background rounded-lg border border-green-200">
                    <div className="text-sm text-muted-foreground mb-1">Expected Proceeds</div>
                    <div className="text-2xl font-bold text-green-600">{formatCurrency(sellValue, position.currency)}</div>
                  </div>
                  <div className="p-4 bg-background rounded-lg border border-green-200">
                    <div className="text-sm text-muted-foreground mb-1">Remaining Position</div>
                    <div className="text-xl font-bold text-foreground">
                      {(position.quantity - recommendedSellQty).toFixed(4)} shares
                    </div>
                    <div className="text-sm text-muted-foreground">
                      Value: {formatCurrency((position.quantity - recommendedSellQty) * position.currentPrice, position.currency)}
                    </div>
                  </div>
                </CardContent>
              </Card>
            )}
          </div>

          {/* Profit Targets */}
          <Card>
            <CardHeader>
              <CardTitle className="text-lg flex items-center gap-2">
                <Target className="h-5 w-5 text-green-600" />
                Profit Targets (Reward:Risk Ratios)
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
                {profitTargets.map((target, idx) => {
                  const reached = position.currentPrice >= target.price;
                  const isNext = target === nextTarget;
                  return (
                    <div
                      key={idx}
                      className={cn(
                        "p-4 rounded-lg border-2 transition-all",
                        reached ? "bg-green-500/10 border-green-500" : 
                        isNext ? "bg-primary/10 border-primary" :
                        "bg-muted/30 border-border"
                      )}
                    >
                      <div className="flex items-center justify-between mb-2">
                        <span className="font-bold text-foreground">{target.ratio}</span>
                        {reached && <Badge className="bg-green-600">Reached ✓</Badge>}
                        {isNext && !reached && <Badge>Next</Badge>}
                      </div>
                      <div className="text-2xl font-bold text-foreground">{formatCurrency(target.price, position.currency)}</div>
                      <div className="text-sm text-muted-foreground">+{target.gain}% gain</div>
                    </div>
                  );
                })}
              </div>
            </CardContent>
          </Card>

          {/* Stop Loss Levels */}
          <Card>
            <CardHeader>
              <CardTitle className="text-lg flex items-center gap-2">
                <ShieldAlert className="h-5 w-5 text-red-600" />
                Stop Loss Protection Levels
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                {stopLosses.map((stop, idx) => (
                  <div key={idx} className="p-4 rounded-lg border-2 border-red-500/30 bg-red-500/5">
                    <div className="font-bold text-foreground mb-1">{stop.type}</div>
                    <div className="text-2xl font-bold text-red-600">{formatCurrency(stop.price, position.currency)}</div>
                    <div className="text-sm text-muted-foreground">{stop.description}</div>
                    <div className="text-xs text-red-600 mt-2">
                      {((1 - stop.price / position.avgCost) * 100).toFixed(1)}% below entry
                    </div>
                  </div>
                ))}
              </div>
              <div className="mt-4 p-3 bg-muted rounded-lg">
                <p className="text-sm text-muted-foreground">
                  <strong>Note:</strong> Place stop-loss orders below these levels to protect your capital. 
                  The 5% stop is aggressive, 10% is standard for swing trading. Technical stops follow support levels.
                </p>
              </div>
            </CardContent>
          </Card>
        </div>
      </DialogContent>
    </Dialog>
  );
}
